import { bilingual, type Lang } from "./i18n";

// When there is no room for a request (a workspace, a render slot), it waits
// in line. The customer sees where they stand and, when the line has moved
// lately, roughly how long that takes.

/** A request's place in line. */
export interface Place {
  /** 1 is next. */
  place: number;
  /** Rounded minutes it may still wait, from how fast the line moved lately; null without that to go on. */
  minutes: number | null;
}

/** A wait in coarse steps: fives up to half an hour, quarters up to an hour, then whole hours. */
export function roughly(ms: number) {
  const m = ms / 60_000;
  if (m <= 30) return Math.max(5, Math.ceil(m / 5) * 5);
  if (m <= 60) return Math.ceil(m / 15) * 15;
  return Math.round(m / 60) * 60;
}

const english = (n: number) =>
  n % 100 >= 11 && n % 100 <= 13
    ? "th"
    : ["th", "st", "nd", "rd"][n % 10] || "th";

export const LINE = bilingual({
  en: {
    /** What follows the place's number: 3rd. */
    suffix: english,
    inLine: "In line",
    place: (n: number) => `${n}${english(n)} in line`,
    about: (minutes: number) =>
      minutes < 60 ? `about ${minutes} min` : `about ${minutes / 60} h`,
    /** Before the place is known. */
    waiting: "Waiting for a free studio",
  },
  es: {
    suffix: (_n: number) => ".º",
    inLine: "En la cola",
    place: (n: number) => `${n}.º en la cola`,
    about: (minutes: number) =>
      minutes < 60
        ? `unos ${minutes} min`
        : minutes === 60
          ? "alrededor de 1 h"
          : `unas ${minutes / 60} h`,
    waiting: "Esperando un estudio libre",
  },
});

/** "3rd in line · about 10 min", or only the place without an estimate. */
export function lineText(p: Place, lang: Lang) {
  const t = LINE[lang];
  return p.minutes === null
    ? t.place(p.place)
    : `${t.place(p.place)} · ${t.about(p.minutes)}`;
}
